// What one document row of the files list shows and offers, derived from the
// file payload and the screen it is rendered on. Pure functions only — the
// components (file-row.tsx, file-card.tsx) and the action runner
// (use-file-row.tsx) read these and never re-derive a predicate themselves.
// The fixed entries of the "⋯" menu have their rules in file-row-menu-rules.ts.
import { hasPdfExtension } from "utils/helperFunctions";
import { extractSignersFromList } from "utils/signatureUtils";
import { formatFileNumberWithSubNumber } from "utils/formatFileNumberDisplay";

import type {
  DateValue,
  FileItem,
  FileSigner,
  RequestSigner,
  SigningRequest,
} from "./file-item";

/** What the screen rendering the list lets its rows do (from the <Files> props). */
export interface RowContext {
  currentUserId: string | undefined;
  // Gates the customer-side signing actions ("Pasirašymo prašymas",
  // "Siųsti el. laišką", "Įkelti pasirašytą dokumentą").
  customerId: string;
  // "document" on a document's own page: its child rows get the
  // send / edit signature request actions.
  source: string;
  isOrderAmendment: boolean;
}

// ---------------------------------------------------------------- signers

export type SignerState = "signed" | "pending" | "rejected" | "withdrawn";

export interface RowSigner {
  key: string;
  name: string;
  state: SignerState;
  // When it was signed, or when the request was made (pending / rejected).
  at: DateValue;
  reason?: string;
  // A pending customer contact on a live Dokobit request: "Priminti".
  canRemind: boolean;
  signatureId?: string;
  isCurrentUser: boolean;
  // This signer is the logged-in user and may sign right now.
  canSign: boolean;
  kind: "contact" | "user" | "click";
}

// Signing actions are offered only while the document can still be signed.
const isLiveDocument = (item: FileItem) =>
  !item.isSigned && !item.withdrawnAt && !item.annulledAt;

function requestOf(item: FileItem): SigningRequest | null {
  return item.dokobit || item.signontab || null;
}

function requestSignerState(signer: RequestSigner): SignerState {
  if (signer.signed) return "signed";
  if (signer.status === "rejected") return "rejected";
  if (signer.status === "withdrawn") return "withdrawn";
  return "pending";
}

// Signed: when it was signed. Pending / rejected: when the request was made.
function requestSignerDate(signer: RequestSigner, state: SignerState, requestedAt: DateValue) {
  if (state === "signed") return signer.signing_time;
  if (state === "withdrawn") return null;
  return requestedAt;
}

function clickSignerState(signer: FileSigner): SignerState {
  if (signer.status === "signed") return "signed";
  if (signer.status === "rejected") return "rejected";
  if (signer.status === "withdrawn") return "withdrawn";
  // "pending", and any status the backend has not finished (no signature yet).
  return "pending";
}

// Pending: when it was requested. Signed / rejected: when the signer acted.
function clickSignerDate(signer: FileSigner, state: SignerState) {
  if (state === "pending") return signer.createdAt;
  if (state === "withdrawn") return null;
  return signer.updatedAt;
}

/** The company users on the signing request; without a request row, from signersList. */
export function getRequestUserSigners(item: FileItem): RequestSigner[] {
  const request = requestOf(item);
  if (request) return request.signersList || [];
  return (item.signersList || []).map((entry) => entry.user || {});
}

/**
 * Everyone asked to sign this document, as one list. Three sources, by sign type:
 *   Dokobit / Signontab → the request's customer contacts, then its company users;
 *   Click               → the file-level signersList.
 * A rejected customer contact is shown as rejected (it used to read "Laukiama").
 */
export function getRowSigners(item: FileItem, ctx: RowContext): RowSigner[] {
  const isDokobit = item.signType === "Dokobit";
  const isRequestType = isDokobit || item.signType === "Signontab";
  const request = requestOf(item);
  const requestedAt = request?.createdAt ?? null;
  const signers: RowSigner[] = [];

  if (isRequestType) {
    const contacts = request?.contactSigners || [];
    const users = getRequestUserSigners(item);

    contacts.forEach((contact, index) => {
      const state = requestSignerState(contact);
      signers.push({
        key: `contact-${contact.id ?? index}`,
        name: contact.name || "",
        state,
        at: requestSignerDate(contact, state, requestedAt),
        reason: contact.declineReason,
        canRemind: Boolean(
          !contact.signed &&
            contact.signatureId &&
            contact.status !== "rejected" &&
            isDokobit &&
            isLiveDocument(item)
        ),
        signatureId: contact.signatureId,
        isCurrentUser: false,
        canSign: false,
        kind: "contact",
      });
    });
    users.forEach((user, index) => {
      const state = requestSignerState(user);
      const isCurrentUser = Boolean(user.id) && user.id === ctx.currentUserId;
      signers.push({
        key: `user-${user.id ?? index}`,
        name: user.name || "",
        state,
        at: requestSignerDate(user, state, requestedAt),
        reason: user.declineReason,
        canRemind: false,
        signatureId: user.signatureId,
        isCurrentUser,
        canSign:
          !user.signed &&
          user.status !== "rejected" &&
          isCurrentUser &&
          isDokobit &&
          isLiveDocument(item),
        kind: "user",
      });
    });
  }

  if (item.signType === "Click") {
    const signingOpen = !item.withdrawnAt && !item.annulledAt;
    for (const signer of item.signersList || []) {
      signers.push(toClickRowSigner(signer, ctx.currentUserId, signingOpen));
    }
  }

  return signers;
}

/**
 * One entry of a file-level signersList as a row signer. It can be signed
 * when it is the logged-in user's own pending line and `signingOpen` (the
 * screen's own condition: a live document, not a read-only view).
 */
export function toClickRowSigner(
  signer: FileSigner,
  currentUserId: string | undefined,
  signingOpen: boolean
): RowSigner {
  const state = clickSignerState(signer);
  const isCurrentUser = Boolean(signer.user?.id) && signer.user?.id === currentUserId;
  return {
    key: `click-${signer.id}`,
    name: signer.user?.name || "",
    state,
    at: clickSignerDate(signer, state),
    reason: signer.declineReason,
    canRemind: false,
    isCurrentUser,
    canSign: signer.status === "pending" && isCurrentUser && signingOpen,
    kind: "click",
  };
}

// ------------------------------------------------------------ status pill

/** The document fields the status pill reads (a leave request's attached file has just these). */
export type StatusPillSource = Pick<
  FileItem,
  | "annulledAt"
  | "withdrawnAt"
  | "isSigned"
  | "isApproved"
  | "isApprovalRequired"
  | "filesTemplateCustomField"
>;

/**
 * The document's template needs a manager's approval. The customer list sends
 * the plain `isApprovalRequired` flag; other payloads still carry the template
 * JSON it is read from.
 */
export function isApprovalRequired(item: StatusPillSource): boolean {
  return (
    item.isApprovalRequired === true ||
    item.filesTemplateCustomField?.templateEntity?.isApprovalRequired === true
  );
}

export type StatusPill =
  | { kind: "annulled" }
  | { kind: "withdrawn" }
  | { kind: "signed" }
  | { kind: "rejected" }
  | { kind: "approval_required" }
  | { kind: "progress"; signed: number; total: number };

/**
 * The one status shown before the title. Rules are checked top to bottom and
 * the first that applies wins; a document that matches none gets no pill.
 */
export function getStatusPill(item: StatusPillSource, signers: RowSigner[]): StatusPill | null {
  if (item.annulledAt) return { kind: "annulled" };
  if (item.withdrawnAt) return { kind: "withdrawn" };
  if (item.isSigned) return { kind: "signed" };
  if (signers.some((signer) => signer.state === "rejected")) return { kind: "rejected" };
  if (isApprovalRequired(item) && !item.isApproved) return { kind: "approval_required" };
  if (signers.length > 0) {
    const signed = signers.filter((signer) => signer.state === "signed").length;
    return { kind: "progress", signed, total: signers.length };
  }
  return null;
}

/** "Be pasirašymo" on the phone card: no sign type and nobody asked to sign. */
export function isWithoutSigning(item: FileItem, signers: RowSigner[]): boolean {
  return !item.signType && signers.length === 0;
}

// --------------------------------------------------------------- actions

export type RowAction =
  // The logged-in user's own pending signature.
  | "sign_dokobit"
  | "sign_click"
  // "Pasirašymo prašymas": an uploaded PDF, a two-step template, a one-step template.
  | "sign_request_upload"
  | "sign_request_two_step"
  | "sign_request_one_step"
  // "Siųsti pasirašymo prašymą": a child document without signers, on its parent's page.
  | "send_signature_request"
  // Manual signing: e-mail the PDF to the customer, then upload the signed copy.
  | "send_email"
  | "upload_signed"
  // "Redaguoti pasirašymo užklausą" on a child row of a document's page
  // (shown disabled while isEditRequestLocked).
  | "edit_sign_request";

/** The template the document was built from, whether the payload populated it or not. */
export function templateIdOf(item: FileItem): string | undefined {
  if (item.templateId && typeof item.templateId === "object") return item.templateId.id;
  return item.templateId || undefined;
}

function hasTemplateBackedFile(item: FileItem, ctx: RowContext): boolean {
  // System order amendments have no DB templateId but sign like template PDFs.
  return Boolean(templateIdOf(item)) || ctx.isOrderAmendment;
}

/** Every signing action this row offers right now, in no particular order. */
export function getAvailableActions(
  item: FileItem,
  ctx: RowContext,
  signers: RowSigner[]
): RowAction[] {
  const actions: RowAction[] = [];
  const live = isLiveDocument(item);
  const templateBacked = hasTemplateBackedFile(item, ctx);
  const hasDokobitFileToken = Boolean(item.dokobit?.fileToken);
  const canMakeNewRequest = item.dokobit ? item.dokobit.canMakeNewRequest : true;
  const { contactSigners } = extractSignersFromList(item.signersList);
  // Only enriched payloads carry the dokobit / signontab keys (null without a
  // request); rows from a raw payload lack them. A new request on a
  // document whose signing state is unknown, or that is already being signed
  // another way, restarts signing and the backend drops collected signatures.
  const signingStateKnown = "dokobit" in item || "signontab" in item;
  const otherSigningRuns =
    Boolean(item.signontab) || item.signType === "Click" || item.signType === "Signontab";

  if (signers.some((signer) => signer.canSign && signer.kind === "user")) {
    actions.push("sign_dokobit");
  }
  if (signers.some((signer) => signer.canSign && signer.kind === "click")) {
    actions.push("sign_click");
  }

  if (
    signingStateKnown &&
    !otherSigningRuns &&
    !templateBacked &&
    ctx.customerId &&
    live &&
    hasPdfExtension(item.title || "") &&
    item.fileId &&
    !hasDokobitFileToken &&
    item.signType !== "Manual"
  ) {
    actions.push("sign_request_upload");
  }
  if (
    signingStateKnown &&
    templateBacked &&
    ctx.customerId &&
    live &&
    item.fileId &&
    item.signingStepsRequired === 2 &&
    !hasDokobitFileToken &&
    item.signType === "Dokobit"
  ) {
    actions.push("sign_request_two_step");
  }
  if (
    signingStateKnown &&
    ctx.customerId &&
    contactSigners.length > 0 &&
    templateBacked &&
    live &&
    item.signType === "Dokobit" &&
    canMakeNewRequest &&
    item.signingStepsRequired === 1
  ) {
    actions.push("sign_request_one_step");
  }

  if (ctx.customerId && item.signType === "Manual" && live) {
    actions.push("send_email", "upload_signed");
  }

  if (ctx.source === "document" && live) {
    if (
      !item.dokobit?.signersPayload &&
      item.signersList?.length === 0 &&
      item.signType !== "Signontab"
    ) {
      actions.push("send_signature_request");
    }
    if (
      item.dokobit?.signersPayload ||
      item.signType === "Click" ||
      item.signType === "Signontab"
    ) {
      actions.push("edit_sign_request");
    }
  }

  return actions;
}

export interface RowActionInfo {
  action: RowAction;
  labelKey: string;
  primaryRank?: number;
  menuRank: number;
}

// Where each action goes and what it is called. How it is read:
//   - the row's one highlighted button is the available action with the
//     lowest primaryRank; an action without primaryRank never gets it;
//   - every other available action goes to the "⋯" menu, by menuRank.
// "Pasirašymo prašymas" on a plain uploaded PDF stays in the menu: most
// customer documents are unsigned uploads, and a button on each is noise.
export const ROW_ACTIONS: RowActionInfo[] = [
  { action: "sign_dokobit", labelKey: "sign", primaryRank: 1, menuRank: 3 },
  { action: "sign_click", labelKey: "sign", primaryRank: 2, menuRank: 4 },
  { action: "sign_request_two_step", labelKey: "sign_request", primaryRank: 3, menuRank: 6 },
  { action: "sign_request_one_step", labelKey: "sign_request", primaryRank: 4, menuRank: 7 },
  { action: "send_signature_request", labelKey: "document_send_signature_request", primaryRank: 5, menuRank: 8 },
  { action: "send_email", labelKey: "send_email", primaryRank: 6, menuRank: 9 },
  { action: "sign_request_upload", labelKey: "sign_request", menuRank: 5 },
  { action: "edit_sign_request", labelKey: "edit_sign_request", menuRank: 1 },
  { action: "upload_signed", labelKey: "upload_signed_document", menuRank: 2 },
];

export function pickPrimaryAction(actions: RowAction[]): RowActionInfo | null {
  let primary: RowActionInfo | null = null;
  let primaryRank = Infinity;
  for (const info of ROW_ACTIONS) {
    if (info.primaryRank === undefined || !actions.includes(info.action)) continue;
    if (info.primaryRank < primaryRank) {
      primary = info;
      primaryRank = info.primaryRank;
    }
  }
  return primary;
}

export function getMenuActions(
  actions: RowAction[],
  primary: RowActionInfo | null
): RowActionInfo[] {
  return ROW_ACTIONS.filter(
    (info) => actions.includes(info.action) && info !== primary
  ).sort((a, b) => a.menuRank - b.menuRank);
}

// ------------------------------------------------------------ other facts

/**
 * "SF-12" for the meta line, or "" when the document has no real number.
 * Uploads are stored with number 0, which means "none" here.
 */
export function getRowNumber(item: FileItem): string {
  const number = item.number === 0 || item.number === "0" ? null : item.number;
  const formatted = formatFileNumberWithSubNumber(number, item.subNumber);
  return `${item.series?.title || ""}${formatted}`;
}

/** Category title for the meta line — only when the payload carries it. */
export function getCategoryTitle(item: FileItem): string {
  return item.category && typeof item.category === "object"
    ? item.category.title || ""
    : "";
}

/**
 * A checkbox goes on top-level documents only: not on a nested related row,
 * nor on a child document a payload lists at the top level.
 */
export function isSelectableRow(item: FileItem, isChild: boolean): boolean {
  return !isChild && !item.relatedTo;
}

/** "Peržiūrėti" opens the in-app PDF viewer, so only for a stored PDF. */
export function canViewFile(item: FileItem): boolean {
  return Boolean(hasPdfExtension(item.title || "") && item.fileId);
}
